import { firstValueFrom, type Observable } from 'rxjs';

interface RailPage {
  readonly count: number;
  readonly items: readonly { readonly id: string; readonly accountId?: string }[];
}

/**
 * Reads every page of a rail row's list, 100 at a time, and answers the ids it holds: the exact set a bounded
 * action then acts on. Any sign that the list moved or that a page is not this tenant's stops it before a
 * write: a count that changes between pages, an empty page before the count is reached, a repeated id, an
 * item of another tenant.
 */
export async function collectRailIds(
  tenant: string,
  page: (offset: number, limit: number) => Observable<RailPage>,
): Promise<string[]> {
  const ids = new Set<string>();
  let offset = 0;
  let count: number | undefined;
  do {
    const result = await firstValueFrom(page(offset, 100));
    if (count !== undefined && result.count !== count) throw Error('This view changed. Please try again.');
    count = result.count;
    if (!Number.isInteger(count) || count < 0 || (!result.items.length && offset < count))
      throw Error('Unable to read this view completely.');
    for (const item of result.items) {
      if (!item.id || item.accountId !== tenant || ids.has(item.id))
        throw Error('This view changed. Please try again.');
      ids.add(item.id);
    }
    offset += result.items.length;
    if (offset > count) throw Error('This view changed. Please try again.');
  } while (offset < count);
  return [...ids];
}
